import React, { useEffect, useState } from 'react';
import { supabase } from '../supabaseClient';
import { useCart } from '../context/CartContext';
import toast from 'react-hot-toast';
import { getFigurineImageSrc } from '../utils/figurineMedia';
import { FigureCard, RarityBadge, SectionHeading } from '../components/BrandUI';

export default function BrowsePage() {
  const minPrice = 100;
  const maxPrice = 600;
  const [figurines, setFigurines] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [search, setSearch] = useState('');
  const [seriesFilter, setSeriesFilter] = useState(null);
  const [priceRange, setPriceRange] = useState([minPrice, maxPrice]);
  const [priceInputs, setPriceInputs] = useState([String(minPrice), String(maxPrice)]);
  const [inStockOnly, setInStockOnly] = useState(true);
  const [selectedFigure, setSelectedFigure] = useState(null);
  const allSeries = [
    'Attack on titan', 'Chainsaw Man', 'Dandadan', 'Demon Slayer',
    'Dr.Stone', 'Dragon Ball', 'Frieren', 'JoJo', 'Jujutsu Kaisen',
    'My Hero Academia', 'Naruto', 'One Piece', 'One Punch Man', 'Re:Zero',
    'Sakamoto days', 'TOKYO Revengers',
  ];

  const { addToCart } = useCart();

  useEffect(() => {
    const fetchFigurines = async () => {
      const { data, error } = await supabase
        .from('figurines')
        .select('*')
        .order('name', { ascending: true });

      if (error) {
        console.error('Error fetching figures:', error);
        return;
      }

      setFigurines(data);
      setFiltered(data);
    };

    fetchFigurines();
  }, []);

  useEffect(() => {
    let result = figurines;

    if (search.trim()) {
      result = result.filter(
        (fig) =>
          fig.name.toLowerCase().includes(search.toLowerCase()) ||
          fig.series.toLowerCase().includes(search.toLowerCase())
      );
    }

    if (seriesFilter) {
      result = result.filter((fig) => fig.series === seriesFilter);
    }

    if (inStockOnly) {
      result = result.filter((fig) => fig.stock > 0);
    }

    result = result.filter(
      (fig) => fig.value >= priceRange[0] && fig.value <= priceRange[1]
    );

    setFiltered(result);
  }, [figurines, search, seriesFilter, priceRange, inStockOnly]);

  const clearFilters = () => {
    setSearch('');
    setSeriesFilter(null);
    setPriceRange([minPrice, maxPrice]);
    setPriceInputs([String(minPrice), String(maxPrice)]);
    setInStockOnly(false);
  };

  return (
    <div className="nfig-page">
      <main className="nfig-container">
        <div className="mb-10 text-center">
          <p className="nfig-kicker mb-3">フィギュア / The collection</p>
          <h1 className="nfig-page-title">Find Your Figure</h1>
          <p className="mx-auto mt-4 max-w-2xl text-slate-400">Search every hero, villain, and rare pull currently in the NFig collection.</p>
        </div>

        <section className="nfig-panel mb-10 rounded-2xl p-5 sm:p-7" aria-label="Figure filters">
          <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-end">
            <label>
              <span className="mb-2 block text-xs font-black uppercase tracking-[0.16em] text-slate-300">Search collection</span>
              <div className="relative">
                <svg className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-blue-300" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" />
                </svg>
                <input
                  type="text"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Search by name or series..."
                  className="nfig-input pl-12"
                />
              </div>
            </label>

            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => setInStockOnly((current) => !current)}
                className={inStockOnly ? 'nfig-button-primary' : 'nfig-button-secondary'}
              >
                <span className={`h-2 w-2 rounded-full ${inStockOnly ? 'bg-white' : 'bg-slate-500'}`} />
                {inStockOnly ? 'Show all figures' : 'Show in-stock only'}
              </button>
              <button type="button" onClick={clearFilters} className="nfig-button-danger">Clear Filters</button>
            </div>
          </div>

          <div className="my-6 h-px bg-gradient-to-r from-transparent via-blue-400/30 to-transparent" />

          <div className="grid gap-6 xl:grid-cols-[1fr_auto] xl:items-end">
            <div>
              <p className="mb-3 text-xs font-black uppercase tracking-[0.16em] text-slate-300">Series</p>
              <div className="flex flex-wrap gap-2">
                {allSeries.map((series) => (
                  <button
                    type="button"
                    key={series}
                    onClick={() => setSeriesFilter(seriesFilter === series ? null : series)}
                    className={`rounded-md border px-3 py-2 text-xs font-bold transition ${
                      seriesFilter === series
                        ? 'border-blue-300 bg-[#1683f3] text-white shadow-[0_0_18px_rgba(22,131,243,0.25)]'
                        : 'border-white/10 bg-white/[0.035] text-slate-400 hover:border-blue-400/40 hover:text-white'
                    }`}
                  >
                    {series}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 sm:w-[360px]">
              <label>
                <span className="mb-2 block text-xs font-black uppercase tracking-[0.16em] text-slate-300">Minimum price</span>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-blue-300">₪</span>
                  <input
                    type="number"
                    min="0"
                    max={priceRange[1]}
                    value={priceInputs[0]}
                    onChange={(event) => {
                      const input = event.target.value;
                      setPriceInputs([input, priceInputs[1]]);

                      if (input !== '') {
                        const value = Number(input);
                        if (value >= 0 && value <= priceRange[1]) {
                          setPriceRange([value, priceRange[1]]);
                        }
                      }
                    }}
                    onBlur={() => setPriceInputs([String(priceRange[0]), priceInputs[1]])}
                    className="nfig-input pl-9"
                  />
                </div>
              </label>

              <label>
                <span className="mb-2 block text-xs font-black uppercase tracking-[0.16em] text-slate-300">Maximum price</span>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-blue-300">₪</span>
                  <input
                    type="number"
                    min={priceRange[0]}
                    value={priceInputs[1]}
                    onChange={(event) => {
                      const input = event.target.value;
                      setPriceInputs([priceInputs[0], input]);

                      if (input !== '') {
                        const value = Number(input);
                        if (value >= priceRange[0]) {
                          setPriceRange([priceRange[0], value]);
                        }
                      }
                    }}
                    onBlur={() => setPriceInputs([priceInputs[0], String(priceRange[1])])}
                    className="nfig-input pl-9"
                  />
                </div>
              </label>
            </div>
          </div>
        </section>

        <section>
          <SectionHeading
            eyebrow={`${filtered.length} results`}
            title={seriesFilter || 'All Figures'}
            description={search ? `Matching “${search}”` : 'Select a figure to see details and add it to your cart.'}
          />
          {filtered.length === 0 ? (
            <div className="nfig-panel rounded-xl p-12 text-center">
              <p className="text-xl font-bold text-white">No figures match these filters.</p>
              <button type="button" onClick={clearFilters} className="nfig-button-secondary mt-5">Reset filters</button>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:gap-6 lg:grid-cols-4 xl:grid-cols-5">
              {filtered.map((figure) => (
                <FigureCard key={figure.id} figure={figure} onClick={() => setSelectedFigure(figure)} />
              ))}
            </div>
          )}
        </section>
      </main>

      {selectedFigure && (
        <div className="nfig-modal-backdrop" onClick={() => setSelectedFigure(null)}>
          <div className="nfig-modal max-w-3xl overflow-hidden p-0" onClick={(event) => event.stopPropagation()}>
            <button type="button" onClick={() => setSelectedFigure(null)} className="nfig-close" aria-label="Close figure details">×</button>
            <div className="grid md:grid-cols-2">
              <div className="relative flex min-h-80 items-center justify-center overflow-hidden bg-gradient-to-br from-slate-50 to-blue-100 p-6">
                <div className="absolute inset-0 opacity-30 [background-image:repeating-linear-gradient(120deg,transparent_0_25px,rgba(22,131,243,0.2)_26px,transparent_27px_50px)]" />
                <img src={getFigurineImageSrc(selectedFigure)} alt={selectedFigure.name} className="relative z-10 h-full max-h-[430px] w-full object-contain" />
                {selectedFigure.stock === 0 && <span className="nfig-stock-badge">Pre-order</span>}
              </div>
              <div className="flex flex-col justify-center p-7 sm:p-9">
                <p className="nfig-kicker">{selectedFigure.series}</p>
                <h2 className="mt-2 text-3xl font-black leading-tight text-white">{selectedFigure.name}</h2>
                <div className="mt-5 flex items-center justify-between border-y border-white/10 py-4">
                  <RarityBadge rating={selectedFigure.rating} />
                  <p className="text-3xl font-black text-blue-300">₪{selectedFigure.value}</p>
                </div>
                <p className="mt-5 text-sm leading-relaxed text-slate-400">
                  {selectedFigure.stock > 0 ? 'In stock and ready to join your collection.' : 'Available to reserve as a pre-order.'}
                </p>
                <button
                  type="button"
                  onClick={() => {
                    addToCart(selectedFigure);
                    toast.success(`${selectedFigure.name} added to cart!`);
                  }}
                  className="nfig-button-primary mt-7 w-full"
                >
                  Add to Cart
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
